import { api } from "@/lib/apiClient";

export interface CountryOption {
  id: number;
  name: string;
  iso2?: string | null;
  iso3?: string | null;
  currencyId?: number | null;
  currencyCode?: string | null;
}

export interface StateOption {
  id: number;
  countryId: number;
  name: string;
  code?: string | null;
}

type CountryApiRecord = {
  id: number | string;
  name: string;
  iso2?: string | null;
  iso3?: string | null;
  currency_id?: number | string | null;
  currency_code?: string | null;
};
type StateApiRecord = {
  id: number | string;
  country_id: number | string;
  name: string;
  code?: string | null;
};

function extractRows<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[];
  if (body && typeof body === "object") {
    const data = (body as Record<string, unknown>).data;
    if (Array.isArray(data)) return data as T[];
  }
  return [];
}

let countriesCache: CountryOption[] | null = null;
const statesCache = new Map<number, StateOption[]>();

export async function fetchCountries(): Promise<CountryOption[]> {
  if (countriesCache) return countriesCache;
  const response = await api.get("/api/v1/geo/countries");
  const rows = extractRows<CountryApiRecord>(response.data);
  countriesCache = rows.map((row) => ({
    id: Number(row.id),
    name: row.name,
    iso2: row.iso2 ?? null,
    iso3: row.iso3 ?? null,
    currencyId: row.currency_id != null ? Number(row.currency_id) : null,
    currencyCode: row.currency_code ?? null,
  }));
  return countriesCache;
}

export async function fetchStates(countryId: number): Promise<StateOption[]> {
  if (!countryId) return [];
  const cached = statesCache.get(countryId);
  if (cached) return cached;
  const response = await api.get("/api/v1/geo/states", { params: { country_id: countryId } });
  const rows = extractRows<StateApiRecord>(response.data);
  const mapped = rows.map((row) => ({
    id: Number(row.id),
    countryId: Number(row.country_id),
    name: row.name,
    code: row.code ?? null,
  }));
  statesCache.set(countryId, mapped);
  return mapped;
}

function mapStateRecord(row: StateApiRecord): StateOption {
  return {
    id: Number(row.id),
    countryId: Number(row.country_id),
    name: row.name,
    code: row.code ?? null,
  };
}

export function invalidateStatesCache(countryId?: number) {
  if (countryId == null) {
    statesCache.clear();
    return;
  }
  statesCache.delete(countryId);
}

export async function createState(payload: {
  countryId: number;
  name: string;
  code?: string | null;
}): Promise<StateOption> {
  const response = await api.post("/api/v1/geo/states", {
    country_id: payload.countryId,
    name: payload.name,
    code: payload.code || null,
  });
  const body = response.data as { data?: StateApiRecord } | StateApiRecord;
  const row = body && typeof body === "object" && "data" in body && body.data ? body.data : (body as StateApiRecord);
  const mapped = mapStateRecord(row);
  const current = statesCache.get(mapped.countryId) ?? [];
  const next = current.some((row) => row.id === mapped.id)
    ? current.map((row) => (row.id === mapped.id ? mapped : row))
    : [...current, mapped].sort((a, b) => a.name.localeCompare(b.name));
  statesCache.set(mapped.countryId, next);
  return mapped;
}

export function countryFlagEmoji(iso2?: string | null, countryName?: string | null): string {
  if (iso2 && iso2.trim().length === 2) {
    const code = iso2.trim().toUpperCase();
    const first = String.fromCodePoint(code.charCodeAt(0) + 127397);
    const second = String.fromCodePoint(code.charCodeAt(1) + 127397);
    return `${first}${second}`;
  }
  if (countryName) {
    const n = countryName.toLowerCase().trim();
    if (n.includes("japan")) return "🇯🇵";
    if (n.includes("united kingdom") || n.includes("uk") || n.includes("britain")) return "🇬🇧";
    if (n.includes("germany")) return "🇩🇪";
    if (n.includes("united states") || n.includes("usa") || n.includes("us")) return "🇺🇸";
    if (n.includes("united arab emirates") || n.includes("uae") || n.includes("dubai")) return "🇦🇪";
    if (n.includes("thailand")) return "🇹🇭";
    if (n.includes("south korea") || n.includes("korea")) return "🇰🇷";
    if (n.includes("china")) return "🇨🇳";
    if (n.includes("canada")) return "🇨🇦";
    if (n.includes("australia")) return "🇦🇺";
    if (n.includes("pakistan")) return "🇵🇰";
  }
  return "🌐";
}
